/**
 * useProfessionalTemplates Hook
 *
 * FERP Sprint 5 - Professional Features
 *
 * Fetches professional role-based workflow templates from backend
 *
 * Architecture Compliance:
 * - ✅ Repository pattern via GraphQL query
 * - ✅ Domain-agnostic (all data from backend)
 * - ✅ Life-critical safety (error handling)
 * - ✅ Multi-tenant security (tenant from context)
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCurrentTenant } from './useCurrentTenant';

/**
 * GraphQL query for professional templates
 *
 * Maps to backend ProfessionalTemplatesQuery::professional_templates
 * Source: backend/src/graphql/professional_features_schema.rs
 */
const PROFESSIONAL_TEMPLATES_QUERY = gql`
  query ProfessionalTemplates($input: ProfessionalTemplatesInput!) {
    professionalTemplates(input: $input) {
      templates {
        id
        tenant
        version
        role
        name
        description
        category
        domain
        complexity
        estimatedDurationHours
        downloadCount
        rating
        reviewCount
        createdAt
      }
      totalCount
    }
  }
`;

/**
 * Professional template entity (frontend representation)
 *
 * Maps to backend ProfessionalTemplateGql
 */
export interface ProfessionalTemplate {
  id: string;
  tenant: string;
  version: string;
  role: string;
  name: string;
  description: string;
  category: string;
  domain: string;
  complexity: string;
  estimatedDurationHours: number;
  downloadCount: number;
  rating: number;
  reviewCount: number;
  createdAt: number; // Unix timestamp (milliseconds)
}

/**
 * Hook options for filtering templates
 */
export interface UseProfessionalTemplatesOptions {
  role?: string; // Filter by role (e.g., "HEALTHCARE_ADMINISTRATOR")
  domain?: string; // Filter by domain (e.g., "Healthcare")
  category?: string; // Filter by category (e.g., "Workflow Management")
  featuredOnly?: boolean; // Show only featured templates (rating >= 4.5, downloads >= 100)
}

/**
 * Hook return type
 */
export interface UseProfessionalTemplatesResult {
  templates: ProfessionalTemplate[];
  totalCount: number;
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<any>;
}

/**
 * useProfessionalTemplates Hook
 *
 * Fetches professional templates from backend with filtering
 *
 * @param options - Filter options (role, domain, category, featuredOnly)
 * @returns Templates data, loading state, error state, refetch function
 *
 * @example
 * ```tsx
 * function ProfessionalLandingPage() {
 *   const { user } = useAuth();
 *   const { templates, loading, error } = useProfessionalTemplates({
 *     role: user?.role,
 *     featuredOnly: false
 *   });
 *
 *   if (loading) return <Loader />;
 *   if (error) return <Alert>Error: {error.message}</Alert>;
 *
 *   return (
 *     <Grid>
 *       {templates.map(template => (
 *         <TemplateCard key={template.id} template={template} />
 *       ))}
 *     </Grid>
 *   );
 * }
 * ```
 */
export function useProfessionalTemplates(
  options: UseProfessionalTemplatesOptions = {}
): UseProfessionalTemplatesResult {
  // ✅ FERP-Compliant: Get tenant from context (not hardcoded)
  const { tenantId } = useCurrentTenant();

  // Execute GraphQL query
  const { data, loading, error, refetch } = useQuery<Record<string, any>>(PROFESSIONAL_TEMPLATES_QUERY, {
    variables: {
      input: {
        tenant: tenantId,
        role: options.role || null,
        domain: options.domain || null,
        category: options.category || null,
        featuredOnly: options.featuredOnly || false,
      },
    },
    fetchPolicy: 'cache-and-network', // Always check server for updates
    skip: !tenantId, // Don't query until tenant is available
  });

  // Extract templates from response
  const templates = data?.professionalTemplates?.templates || [];
  const totalCount = data?.professionalTemplates?.totalCount || 0;

  return {
    templates,
    totalCount,
    loading,
    error,
    refetch,
  };
}
